import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './kpi-tile.module.scss';
import DemoBadge from '@/components/demo-badge/demo-badge';
import { DemoBadgeVariant } from '@/components/demo-badge/demo-badge.types';
import { kpiDeltaArrow } from './kpi-tile.constants';
import { KpiDelta, KpiTileVariant } from './kpi-tile.types';

interface KpiTileProps {
  label: string;
  value: string;
  unit?: string;
  note?: string;
  delta?: KpiDelta;
  /** Names the compared span. Only meaningful alongside `delta`. */
  deltaCaption?: string;
  variant?: KpiTileVariant;
  /** Marks the tile's figure as absent. Sits by the label, not by the value. */
  demoBadge?: DemoBadgeVariant;
  className?: string;
}

export default function KpiTile({
  label,
  value,
  unit,
  note,
  delta,
  deltaCaption,
  variant = 'default',
  demoBadge,
  className,
}: KpiTileProps) {
  const { t } = useTranslation('metrics');

  return (
    <div className={clsx(styles.tile, styles[`tile-${variant}`], className)}>
      <div className={styles.label}>
        {label}
        {demoBadge && <DemoBadge variant={demoBadge} />}
      </div>
      <div className={styles.value}>
        {value}
        {unit && <small className={styles.unit}> {unit}</small>}
      </div>
      {/* Separate lines: run together, the delta reads as the share the note
          describes rather than a move against the previous period. */}
      <div className={styles.note}>
        {delta && (
          <span
            role="img"
            // The glyph and its colour are the only things separating a rise from a fall, so
            // the direction has to reach a screen reader as a word.
            aria-label={t(`kpiTile.deltaLabel.${delta.direction}`, { value: delta.value })}
            className={styles[`delta-${delta.direction}`]}
          >
            {`${kpiDeltaArrow[delta.direction]} ${delta.value}`}
          </span>
        )}
        {delta && (
          <span className={styles.deltaCaption}>{deltaCaption ?? t('kpiTile.deltaCaption')}</span>
        )}
      </div>
      {note && <div className={styles.note}>{note}</div>}
    </div>
  );
}
